/* ══════════════════════════════════════════════════════════════════════
   WD / VPO / IPS COVERAGE SHEET

   Stacked header rows with MERGED product groups:

     band    |        10                      |          20              |
     product | Campa 200 ML PET | KO 250 ML.. | Campa 500 ML PET | ...   |
     metric  | WD% |      | VPO | IPS | VPO/IPS | WD% |      | VPO | IPS |
     period  |Jul-26|vs Jan|Jul-26|Jul-26|      |Jul-26|vs Jan| ...      |
     data    | All India | 27 | 4 | 4.3 | 3.3 | 1.3 | 22 | 6 | ...       |

   Everything about the layout is DETECTED, not assumed, because these
   exports vary: the label column may not be column A (the DDR sheets in
   the same workbook carry a blank spacer column), the product row may not
   sit directly above the metric row, and the band row may be missing.

   VPO/IPS is computed as VPO ÷ IPS rather than read — only the first group
   labels that column. Verified: Campa 200 All India 4.3 / 3.3 = 1.3 ✓
   ══════════════════════════════════════════════════════════════════════ */

export const WD_SHEET = /\bwd\b|vpo|ips|coverage|pet|distribution/i;

const txt = (v) => (v === null || v === undefined ? "" : String(v).trim());

/** null (not 0) for blanks — genuine gaps must stay empty, not read as zero. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/,/g, "").replace(/%/g, "").replace(/\s/g, ""));
  return isNaN(n) ? null : n;
};

const isNumeric = (v) => num(v) !== null;

/** Merged header cells arrive as value-then-blanks. */
function forwardFill(row, width, from = 0) {
  const out = new Array(width).fill("");
  let last = "";
  for (let i = from; i < width; i++) {
    const v = txt(row?.[i]);
    if (v) last = v;
    out[i] = last;
  }
  return out;
}

/* "WD", "WD%", "W D %", "VPO/IPS", "VPO / IPS", "IPS" … */
const METRIC_RE = /^(w\.?\s*d\.?\s*%?|vpo\s*\/\s*ips|vpo|ips)$/i;
const canonMetric = (m) => {
  const s = String(m).toLowerCase().replace(/[\s.]/g, "");
  if (s === "vpo/ips") return "VPOIPS";
  if (s.startsWith("wd")) return "WD";
  if (s === "vpo") return "VPO";
  if (s === "ips") return "IPS";
  return null;
};

/** Scratch calculations sitting below the table are not markets. */
const JUNK_ROW = /^(tu\s*\/?\s*sp|cfl|total|grand\s*total|sum|n\/?a|[\d.,]+%?)$/i;

export function parseWdSheet(raw, sheetName = "") {
  const fail = (why) => ({ __fail: why, sheetName });
  if (!raw?.length) return fail("sheet is empty");

  const width = Math.max(...raw.map(r => (r || []).length), 0);
  if (width < 3) return fail(`only ${width} columns`);

  // ── 1. Metric row: the row carrying the most WD / VPO / IPS tokens ──
  let metricIdx = -1, bestHits = 0;
  for (let i = 0; i < Math.min(30, raw.length); i++) {
    const hits = (raw[i] || []).filter(c => METRIC_RE.test(txt(c))).length;
    if (hits > bestHits) { bestHits = hits; metricIdx = i; }
  }
  if (metricIdx < 0 || bestHits < 2) {
    return fail(`no WD/VPO/IPS header row found (best row had ${bestHits} of them)`);
  }

  const metricsRaw = [];
  for (let i = 0; i < width; i++) metricsRaw.push(txt(raw[metricIdx]?.[i]));

  // ── 2. Label column: the leftmost column whose rows below are text ──
  //    (a blank spacer column A is common in these exports)
  let labelCol = -1;
  for (let c = 0; c < Math.min(6, width); c++) {
    let textRows = 0;
    for (let i = metricIdx + 1; i < Math.min(metricIdx + 25, raw.length); i++) {
      const v = txt(raw[i]?.[c]);
      if (v && !isNumeric(v)) textRows++;
    }
    if (textRows >= 2) { labelCol = c; break; }
  }
  if (labelCol < 0) return fail("no market-name column found below the header");

  // ── 3. Product row: nearest non-empty row above the metric row ──
  let productIdx = -1;
  for (let i = metricIdx - 1; i >= 0 && i >= metricIdx - 4; i--) {
    const row = raw[i] || [];
    const filled = row.filter((c, ci) => ci > labelCol && txt(c)).length;
    if (filled) { productIdx = i; break; }
  }

  // ── 4. Band row: nearest non-empty row above the product row ──
  let bandIdx = -1;
  if (productIdx > 0) {
    for (let i = productIdx - 1; i >= 0 && i >= productIdx - 3; i--) {
      const row = raw[i] || [];
      if (row.filter((c, ci) => ci > labelCol && txt(c)).length) { bandIdx = i; break; }
    }
  }

  const products = productIdx >= 0 ? forwardFill(raw[productIdx], width, labelCol + 1) : [];
  const bands = bandIdx >= 0 ? forwardFill(raw[bandIdx], width, labelCol + 1) : [];

  // ── 5. Period row (Jul-26 / vs Jan) distinguishes value from delta ──
  const periods = [];
  const periodRow = raw[metricIdx + 1] || [];
  for (let i = 0; i < width; i++) periods.push(txt(periodRow[i]));
  const hasPeriodRow = periods.some(p => /vs\s|^[a-z]{3}[-\s]?\d{2}$/i.test(p));
  const dataStart = metricIdx + (hasPeriodRow ? 2 : 1);

  // ── 6. Column groups ──
  //    Groups are keyed by their COLUMN RUN, not by product name: two packs
  //    can share a label across price bands, and keying by name would merge
  //    them and silently drop the second one's VPO/IPS columns.
  const groups = [];
  let current = null;
  let lastProduct = null;

  for (let i = labelCol + 1; i < width; i++) {
    const rawM = metricsRaw[i];
    const product = products[i] || "";
    const m = rawM ? canonMetric(rawM) : null;

    // New group when the pack label changes, or — on sheets with no pack
    // headers — when a metric we already hold repeats.
    const productChanged = product !== lastProduct;
    const metricRepeats = m && m !== "WD" && current && current.cols[m] !== undefined;
    if (!current || (product && productChanged) || (!product && metricRepeats)) {
      current = {
        product: product || `Pack ${groups.length + 1}`,
        band: bands[i] || "", cols: {}, lastMetric: "", span: [],
      };
      groups.push(current);
    }
    lastProduct = product;
    current.span.push(i);
    if (!current.band && bands[i]) current.band = bands[i];

    // Metrics fill forward WITHIN a group only, so IPS never leaks across.
    // "WD%" is typically merged over its value+delta pair, which is exactly
    // what this fill plus the period row resolves.
    const metric = m || current.lastMetric;
    if (!metric) continue;
    current.lastMetric = metric;

    if (metric === "WD") {
      const isDelta = /vs/i.test(periods[i]) || (!rawM && current.cols.WD !== undefined);
      if (isDelta) { if (current.cols.WDd === undefined) current.cols.WDd = i; }
      else if (current.cols.WD === undefined) current.cols.WD = i;
    } else if (metric !== "VPOIPS" && current.cols[metric] === undefined) {
      current.cols[metric] = i;          // VPO/IPS is computed, never read
    }
  }

  /* Positional fallback: if a pack's VPO / IPS never got a label (merged or
     blank header cell), take its next unassigned columns in order. Column
     order in these sheets is always WD, delta, VPO, IPS. */
  groups.forEach(g => {
    const used = new Set(Object.values(g.cols));
    const free = g.span.filter(c => !used.has(c));
    if (g.cols.VPO === undefined && free.length) g.cols.VPO = free.shift();
    if (g.cols.IPS === undefined && free.length) g.cols.IPS = free.shift();
  });

  // Unique display key — duplicate pack labels would collide in the values map
  const seen = {};
  groups.forEach(g => {
    seen[g.product] = (seen[g.product] || 0) + 1;
    g.key = seen[g.product] > 1 ? `${g.product} (${seen[g.product]})` : g.product;
  });

  const valid = groups.filter(g => g.cols.WD !== undefined || g.cols.VPO !== undefined);
  if (!valid.length) return fail("found the metric row but could not group any packs");

  // ── 7. Data rows ──
  const rows = [];
  for (let i = dataStart; i < raw.length; i++) {
    const r = raw[i];
    if (!r) continue;
    const name = txt(r[labelCol]);
    if (!name || JUNK_ROW.test(name) || isNumeric(name)) continue;

    const values = {};
    let any = false;
    valid.forEach(g => {
      const WD  = g.cols.WD  !== undefined ? num(r[g.cols.WD])  : null;
      const WDd = g.cols.WDd !== undefined ? num(r[g.cols.WDd]) : null;
      const VPO = g.cols.VPO !== undefined ? num(r[g.cols.VPO]) : null;
      const IPS = g.cols.IPS !== undefined ? num(r[g.cols.IPS]) : null;
      const VPOIPS = (VPO != null && IPS) ? VPO / IPS : null;
      if (WD != null || VPO != null || IPS != null) any = true;
      values[g.key] = { WD, WDd, VPO, IPS, VPOIPS };
    });
    if (!any) continue;
    rows.push({ market: name, isTotal: /^all\s*india$/i.test(name), values });
  }
  if (!rows.length) return fail("header parsed but no data rows had values");

  return {
    groups: valid.map(g => ({ key: g.key, product: g.product, band: g.band })),
    // Resolved column index per metric — surfaced in the UI so a mis-read
    // header is visible instead of showing as empty cells.
    columnMap: valid.map(g => ({ pack: g.key, band: g.band, cols: g.cols, span: g.span })),
    bands: [...new Set(valid.map(g => g.band).filter(Boolean))],
    rows,
    markets: rows.filter(r => !r.isTotal).map(r => r.market),
    allIndia: rows.find(r => r.isTotal) || null,
    period: periods.find(p => /^[a-z]{3}[-\s]?\d{2}$/i.test(p)) || "",
    deltaLabel: periods.find(p => /vs/i.test(p)) || "vs prior",
    layout: { metricIdx, productIdx, bandIdx, labelCol, dataStart, width },
  };
}

/**
 * Returns { wd, debug } — `debug` lists every sheet probed and why it was
 * rejected, so a miss is diagnosable instead of silent.
 */
export function findWdSheet(workbook, XLSX) {
  const read = (n) => XLSX.utils.sheet_to_json(workbook.Sheets[n],
    { header: 1, raw: true, defval: null });
  const debug = [];

  const attempt = (name) => {
    let parsed;
    try { parsed = parseWdSheet(read(name), name); }
    catch (e) { debug.push({ sheet: name, why: `threw: ${e.message}` }); return null; }
    if (parsed?.__fail) { debug.push({ sheet: name, why: parsed.__fail }); return null; }
    return { ...parsed, sheetName: name };
  };

  const named = workbook.SheetNames.filter(n => WD_SHEET.test(n));
  for (const name of named) {
    const hit = attempt(name);
    if (hit) return { wd: hit, debug };
  }
  for (const name of workbook.SheetNames) {
    if (named.includes(name)) continue;
    const hit = attempt(name);
    if (hit) return { wd: hit, debug };
  }
  return { wd: null, debug };
}